/**
 * Design Tokens: Typography
 *
 * Dual-font system: Libre Baskerville (serif) for display/headings,
 * Source Sans 3 (sans-serif) for body text and UI controls.
 * Editorial, authoritative feel matching the Archer brand.
 */

const fontFamily = {
  display: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
  primary: "'Source Sans 3', 'Helvetica Neue', Helvetica, sans-serif",
  base: "'Source Sans 3', 'Helvetica Neue', Helvetica, sans-serif",
  mono: '"JetBrains Mono", "SF Mono", Consolas, monospace',
} as const

// Change these once to update the application reading roles and size aliases.
const readingFontSize = { primary: '16px', supporting: '14px' } as const

export const typography = {
  fontFamily,

  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },

  fontSize: {
    xs: readingFontSize.supporting,
    sm: readingFontSize.supporting,
    base: readingFontSize.primary,
    md: readingFontSize.primary,
    lg: '18px',
    xl: '20px',
    '2xl': '22px',
    '3xl': '26px',
    '4xl': '36px',
  },

  scale: {
    pageTitle: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '30px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    sectionTitle: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    workspaceTitle: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '20px',
      fontWeight: 400,
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
    },
    statNumber: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '36px',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    subtitle: {
      fontSize: readingFontSize.primary,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    body: {
      fontSize: readingFontSize.primary,
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0em',
    },
    bodySmall: {
      fontSize: readingFontSize.primary,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    caption: {
      fontSize: readingFontSize.supporting,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    captionSmall: {
      fontSize: readingFontSize.supporting,
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: '0.01em',
    },
    button: {
      fontSize: readingFontSize.primary,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    label: {
      fontSize: readingFontSize.supporting,
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const,
    },
    navItem: {
      fontSize: readingFontSize.primary,
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: '0em',
    },
  },

  /**
   * Reading scale for generated artifacts.
   *
   * Artifact content is a reviewable document, not compact application chrome.
   * Relative units preserve browser text resize and keep the scale local to the
   * shared ArtifactReadingSurface instead of enlarging the whole application.
   */
  artifact: {
    title: {
      fontFamily: fontFamily.display,
      fontSize: '1.375rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    sectionTitle: {
      fontFamily: fontFamily.primary,
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    body: {
      fontFamily: fontFamily.primary,
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0em',
    },
    bodyCompact: {
      fontFamily: fontFamily.primary,
      fontSize: '0.9375rem',
      fontWeight: 400,
      lineHeight: 1.55,
      letterSpacing: '0em',
    },
    supporting: {
      fontFamily: fontFamily.primary,
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.005em',
    },
    label: {
      fontFamily: fontFamily.primary,
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: '0.02em',
    },
    table: {
      fontFamily: fontFamily.primary,
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    code: {
      fontFamily: fontFamily.mono,
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0em',
    },
    metric: {
      fontFamily: fontFamily.display,
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      fontVariantNumeric: 'tabular-nums',
    },
  },

  heading: {
    h1: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '30px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '26px',
      fontWeight: 400,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: "'Libre Baskerville', Georgia, 'Times New Roman', serif",
      fontSize: '20px',
      fontWeight: 400,
      lineHeight: 1.35,
      letterSpacing: '-0.005em',
    },
    h5: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: readingFontSize.primary,
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: '0em',
    },
  },

  lineHeight: {
    tight: 1.1,
    snug: 1.2,
    normal: 1.4,
    relaxed: 1.6,
    loose: 1.8,
  },

  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0em',
    wide: '0.01em',
    wider: '0.02em',
    widest: '0.04em',
  },
} as const

interface TypographyStyle {
  fontFamily: string
  fontSize: string
  fontWeight: number
  lineHeight: number
  letterSpacing: string
  textTransform?: 'uppercase'
}

// A stable CSS shape avoids multiplying token-literal unions through MUI's sx types.
export const createTypographyStyle = (scale: keyof typeof typography.scale): TypographyStyle => {
  const style = typography.scale[scale]
  return {
    fontFamily: ('fontFamily' in style ? style.fontFamily : typography.fontFamily.primary) as string,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
    ...('textTransform' in style && { textTransform: style.textTransform }),
  }
}

export const typographyMixin = (scale: keyof typeof typography.scale) => {
  const s = typography.scale[scale]
  const family = 'fontFamily' in s ? (s as any).fontFamily : typography.fontFamily.primary
  return `
  font-family: ${family};
  font-size: ${s.fontSize};
  font-weight: ${s.fontWeight};
  line-height: ${s.lineHeight};
  letter-spacing: ${s.letterSpacing};
  ${'textTransform' in s ? `text-transform: ${s.textTransform};` : ''}
`
}

export default typography
